//src/pages/cadastros/TenantsPage.tsx
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { toast } from "sonner";

import {
  Search,
  Plus,
  ChevronLeft,
  ChevronRight,
  Rocket,
} from "lucide-react";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/shared/PageHeader";
import { EmptyState } from "@/components/shared/EmptyState";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { CrudRowActions } from "@/components/shared/CrudRowActions";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { useSetTenantActive, useTenants } from "@/hooks/useTenants";
import type { TenantListItem } from "@/services/tenants";
import { maskCNPJ, onlyDigits } from "@/lib/masks";
import { useAuth } from "@/contexts/AuthContext";
import { PERMISSIONS } from "@/lib/permissions";
import { PermissionButton } from "@/components/shared/PermissionButton";
import { PermissionIconButton } from "@/components/shared/PermissionIconButton";

type StatusFilter = "active" | "inactive" | "all";

const LIMIT = 50;

function normalizeText(v: string) {
  return (v || "").toLowerCase().trim();
}

function formatCNPJ(value?: string | null) {
  const digits = onlyDigits(value || "");
  if (!digits) return "-";
  return maskCNPJ(digits);
}

export default function TenantsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const userPermissions = user?.permissions ?? [];
  const canReadTenants =
    userPermissions.includes(PERMISSIONS.TENANT_READ) ||
    userPermissions.includes(PERMISSIONS.PLATFORM_TENANTS_READ);
  const canWriteTenants = userPermissions.includes(PERMISSIONS.PLATFORM_TENANTS_WRITE);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("active");
  const [offset, setOffset] = useState(0);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selected, setSelected] = useState<TenantListItem | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setOffset(0);
  }, [debouncedSearch, statusFilter]);

  const query = useTenants({
    q: debouncedSearch,
    limit: LIMIT,
    offset,
  });

  const setActiveMut = useSetTenantActive();
  const allRows = query.data ?? [];

  const filtered = useMemo(() => {
    const q = normalizeText(debouncedSearch);

    let base = allRows;
    if (statusFilter === "active") base = base.filter((r) => r.is_active);
    else if (statusFilter === "inactive") base = base.filter((r) => !r.is_active);

    if (!q) return base;

    return base.filter((r) => {
      const name = normalizeText(r.name);
      const legalName = normalizeText(r.legal_name ?? "");
      const doc = normalizeText(r.document_number ?? "");

      return (
        name.includes(q) ||
        legalName.includes(q) ||
        doc.includes(q) ||
        onlyDigits(doc).includes(onlyDigits(q))
      );
    });
  }, [allRows, debouncedSearch, statusFilter]);

  const hasNextPage = allRows.length === LIMIT;
  const canPrev = offset > 0;
  const isBusy = query.isFetching || setActiveMut.isPending;

  function openToggle(row: TenantListItem) {
    if (!canWriteTenants) return;
    setSelected(row);
    setConfirmOpen(true);
  }

  async function handleConfirmToggle() {
    if (!selected || !canWriteTenants) return;

    const nextActive = !selected.is_active;

    try {
      await setActiveMut.mutateAsync({
        id: selected.id,
        is_active: nextActive,
      });

      toast.success(nextActive ? "Empresa ativada" : "Empresa desativada");
      setConfirmOpen(false);
      setSelected(null);
    } catch (e: any) {
      toast.error(e?.message || "Erro ao alterar status da empresa");
    }
  }

  return (
    <div>
      <PageHeader
        title="Empresas"
        description="Cadastro de empresas (tenants) da plataforma"
      >
        <PermissionButton
          permission={PERMISSIONS.PLATFORM_TENANTS_WRITE}
          onClick={() => navigate("/cadastros/empresa/novo")}
        >
          <Plus className="mr-2 h-4 w-4" />
          Nova Empresa
        </PermissionButton>
      </PageHeader>

      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nome fantasia, razão social ou CNPJ"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
          />
        </div>

        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Ativos</SelectItem>
            <SelectItem value="inactive">Inativos</SelectItem>
            <SelectItem value="all">Todos</SelectItem>
          </SelectContent>
        </Select>

        <div className="ml-auto flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setOffset((o) => Math.max(0, o - LIMIT))}
            disabled={!canPrev || query.isFetching}
          >
            <ChevronLeft className="mr-1 h-4 w-4" />
            Anterior
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={() => setOffset((o) => o + LIMIT)}
            disabled={!hasNextPage || query.isFetching}
          >
            Próxima
            <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
      </div>

      {query.isLoading ? (
        <Card className="p-6 text-sm text-muted-foreground">
          Carregando empresas...
        </Card>
      ) : query.isError ? (
        <Card className="p-6">
          <div className="text-sm text-destructive">
            {(query.error as any)?.message || "Erro ao carregar empresas"}
          </div>
          <div className="mt-3">
            <Button variant="outline" onClick={() => query.refetch()}>
              Tentar novamente
            </Button>
          </div>
        </Card>
      ) : filtered.length === 0 ? (
        <EmptyState
          title="Nenhuma empresa encontrada"
          description="Cadastre uma empresa ou revise os filtros aplicados."
        />
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[280px]">Empresa</TableHead>
                <TableHead className="w-[180px]">CNPJ</TableHead>
                <TableHead className="w-[110px] text-center">Funcionários</TableHead>
                <TableHead className="w-[90px] text-center">Unidades</TableHead>
                <TableHead className="w-[100px] text-center">Status</TableHead>
                <TableHead className="w-[130px] text-center">Habilitação</TableHead>
                <TableHead className="w-[230px] text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {filtered.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-medium">
                    <div className="flex min-w-0 flex-col gap-0.5">
                      <span className="truncate">{r.name}</span>
                      <span className="truncate text-xs text-muted-foreground">
                        {r.legal_name ?? "-"}
                      </span>
                    </div>
                  </TableCell>

                  <TableCell className="text-sm text-muted-foreground">
                    {formatCNPJ(r.document_number)}
                  </TableCell>

                  <TableCell className="text-center text-sm text-muted-foreground">
                    {r.active_employees_count ?? 0}
                  </TableCell>

                  <TableCell className="text-center text-sm text-muted-foreground">
                    {r.units_count ?? 0}
                  </TableCell>

                  <TableCell className="text-center">
                    <Badge
                      variant={r.is_active ? "default" : "secondary"}
                      className="text-xs"
                    >
                      {r.is_active ? "Ativo" : "Inativo"}
                    </Badge>
                  </TableCell>

                  <TableCell className="text-center">
                    <Badge
                      variant={r.onboarding_status === "configured" ? "default" : r.onboarding_status === "in_progress" ? "outline" : "secondary"}
                      className="text-xs"
                    >
                      {r.onboarding_status === "configured" ? "Configurado" : r.onboarding_status === "in_progress" ? "Em onboarding" : "Pendente"}
                    </Badge>
                  </TableCell>

                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <PermissionIconButton
                        label="Configurar acesso inicial"
                        permission={PERMISSIONS.PLATFORM_TENANTS_BOOTSTRAP}
                        disabled={isBusy}
                        onClick={() => navigate(`/cadastros/empresa/${r.id}/onboarding`)}
                      >
                        <Rocket className="h-4 w-4" />
                      </PermissionIconButton>
                    <CrudRowActions
                      onView={() => navigate(`/cadastros/empresa/${r.id}`)}
                      onEdit={() => navigate(`/cadastros/empresa/${r.id}/editar`)}
                      onToggleActive={() => openToggle(r)}
                      isActive={r.is_active}
                      disabled={isBusy}
                      viewPermission={[PERMISSIONS.TENANT_READ, PERMISSIONS.PLATFORM_TENANTS_READ]}
                      editPermission={PERMISSIONS.PLATFORM_TENANTS_WRITE}
                      toggleActivePermission={PERMISSIONS.PLATFORM_TENANTS_WRITE}
                    />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={selected?.is_active ? "Desativar Empresa" : "Ativar Empresa"}
        description={
          selected?.is_active
            ? "Deseja desativar esta empresa?"
            : "Deseja ativar esta empresa novamente?"
        }
        onConfirm={handleConfirmToggle}
        confirmLabel={selected?.is_active ? "Desativar" : "Ativar"}
        variant={selected?.is_active ? "destructive" : "default"}
      />
    </div>
  );
}
